/** Chats (V3Chats): talk to a bot. Answers come from the bot's live data through read-only tools; any change it
 *  suggests arrives as a decision card that only the owner can confirm (docs/12-ux.md §5). */
import { useEffect, useRef, useState } from "react";
import { Crew } from "../components/Crew.tsx";
import { CmdNote, useCommand } from "../components/Decision.tsx";
import { Shell } from "../components/Shell.tsx";
import { Empty, ErrorNote, Page } from "../components/ui.tsx";
import { api } from "../lib/api.ts";
import { useApi, useUserEvents } from "../lib/data.ts";
import { ago } from "../lib/format.ts";
import { Link, navigate } from "../lib/router.tsx";
import type { Bot } from "../lib/types.ts";

interface Proposal { type: string; riskScale?: number; strategyVersionId?: string; why: string; tradeoff: string }
interface Msg { id: string; role: "user" | "assistant"; body: string; proposal?: Proposal | null; created_at: number }

const TITLE: Record<string, string> = { pause: "Pause", resume: "Resume", flatten: "Close everything", set_risk_scale: "Change risk level", switch_strategy: "Switch strategy" };

function ProposalCard({ bot, p }: { bot: Bot; p: Proposal }) {
  const cmd = useCommand(bot.id);
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return <div className="text-[13px] text-muted">Dismissed.</div>;
  const label = p.type === "set_risk_scale" ? `Set risk to ${Math.round((p.riskScale ?? 1) * 100)}%` : TITLE[p.type] ?? p.type;
  return (
    <div className="mt-2 flex flex-col gap-2 rounded-card border border-line bg-card p-4">
      <div className="eyebrow">Decision · needs your OK</div>
      <div className="font-semibold text-ink-strong">{label}</div>
      <div className="text-[14px]">{p.why}</div>
      <div className="rounded-[10px] bg-soft px-3 py-2 text-[14px]">{p.tradeoff}</div>
      <CmdNote state={cmd.state} />
      {cmd.state.phase === "idle" && (
        <div className="flex flex-wrap gap-2">
          <button className={p.type === "flatten" ? "btn-warn" : "btn-primary"} onClick={() => void cmd.send({ type: p.type, ...(p.riskScale !== undefined ? { riskScale: p.riskScale } : {}), ...(p.strategyVersionId ? { strategyVersionId: p.strategyVersionId } : {}) })}>{label}</button>
          <button className="btn" onClick={() => setDismissed(true)}>Not now</button>
        </div>
      )}
    </div>
  );
}

function Conversation({ bot }: { bot: Bot }) {
  const { data, error } = useApi<{ chatId: string; messages: Msg[] }>(`/chats/${bot.id}`);
  const [msgs, setMsgs] = useState<Msg[]>([]), [draft, setDraft] = useState(""), [busy, setBusy] = useState(false), [err, setErr] = useState<Error | null>(null);
  const [streaming, setStreaming] = useState<{ replyId: string; text: string } | null>(null);
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => { if (data) setMsgs(data.messages); }, [data]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs, streaming]);
  useUserEvents((m) => {
    if (m.botId !== bot.id) return;
    if (m.type === "chat.delta") setStreaming((s) => ({ replyId: String(m.replyId), text: (s && s.replyId === m.replyId ? s.text : "") + String(m.text) }));
  });
  const send = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true); setErr(null); setDraft("");
    setMsgs((x) => [...x, { id: `local-${Date.now()}`, role: "user", body: text, created_at: Date.now() }]);
    try {
      const { message } = await api<{ message: Msg }>(`/chats/${bot.id}/messages`, { method: "POST", body: { text } });
      setMsgs((x) => [...x, message]);
    } catch (e) { setErr(e as Error); } finally { setBusy(false); setStreaming(null); }
  };
  return (
    <div className="flex min-h-[60vh] flex-col gap-4">
      <ErrorNote error={error} />
      <div className="flex flex-1 flex-col gap-4" aria-live="polite">
        {msgs.length === 0 && !busy && (
          <div className="flex flex-col gap-3">
            <div className="text-muted">Ask {bot.name} about what it holds, why it traded, or how it is doing. It answers from its own data and can suggest changes for you to confirm.</div>
            <div className="flex flex-wrap gap-2">{["How are you doing this week?", "Why did you trade today?", "What are your biggest bets?", "Lower your risk to 50%"].map((q) => <button key={q} className="btn" onClick={() => setDraft(q)}>{q}</button>)}</div>
          </div>
        )}
        {msgs.map((m) => m.role === "user"
          ? <div key={m.id} className="ml-auto max-w-[80%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-teal px-4 py-2.5 text-white">{m.body}</div>
          : (
            <div key={m.id} className="flex max-w-[90%] gap-3">
              <Crew character={bot.character} size={30} />
              <div className="min-w-0 flex-1">
                <div className="whitespace-pre-wrap text-[15px] leading-relaxed">{m.body}</div>
                {m.proposal && <ProposalCard bot={bot} p={m.proposal} />}
                <div className="mt-1 text-[12px] text-muted">{ago(m.created_at)}</div>
              </div>
            </div>
          ))}
        {busy && (
          <div className="flex max-w-[90%] gap-3"><Crew character={bot.character} size={30} />
            <div className="whitespace-pre-wrap text-[15px] leading-relaxed">{streaming?.text || <span className="text-muted">Looking at my data…</span>}</div></div>
        )}
        <ErrorNote error={err} />
        <div ref={end} />
      </div>
      <form className="sticky bottom-[72px] flex gap-2 bg-bg py-2 md:bottom-0 md:pb-4" onSubmit={(e) => { e.preventDefault(); void send(); }}>
        <input className="input flex-1" placeholder={`Message ${bot.name}`} value={draft} maxLength={2000} onChange={(e) => setDraft(e.target.value)} aria-label={`Message ${bot.name}`} />
        <button className="btn-primary" disabled={busy || !draft.trim()}>{busy ? "…" : "Send"}</button>
      </form>
    </div>
  );
}

export function Chats({ botId }: { botId: string | null }) {
  const { data } = useApi<{ accounts: Bot[] }>("/accounts");
  const bots = data?.accounts ?? [];
  const bot = bots.find((b) => b.id === botId) ?? null;
  useEffect(() => { if (!botId && bots.length && window.innerWidth >= 768) navigate(`/chats/${bots[0]!.id}`, true); }, [botId, bots]);
  return (
    <Shell crumb={<>Chats{bot ? ` · ${bot.name}` : ""}</>}>
      <Page width={1100}>
        {!bots.length ? <Empty title="No bots to talk to yet"><Link to="/bots/new">Start a paper bot</Link> first.</Empty> : (
          <div className="grid gap-6 md:grid-cols-[220px_1fr]">
            <nav aria-label="Bots" className={`flex flex-col gap-1 ${bot ? "hidden md:flex" : ""}`}>
              {bots.map((b) => (
                <Link key={b.id} to={`/chats/${b.id}`} aria-current={b.id === botId ? "page" : undefined}
                  className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-ink no-underline hover:no-underline ${b.id === botId ? "bg-line font-semibold" : "hover:bg-soft"}`}>
                  <Crew character={b.character} size={26} /><span className="flex-1 truncate">{b.name}</span>
                </Link>
              ))}
            </nav>
            {bot ? (
              <section aria-label={`Chat with ${bot.name}`} className="flex flex-col gap-3">
                <div className="flex items-center gap-3 border-b border-line pb-3">
                  <Link to="/chats" className="md:hidden" aria-label="All chats">‹</Link>
                  <Crew character={bot.character} size={36} /><div><div className="font-semibold text-ink-strong">{bot.name}</div><div className="text-[13px] text-muted">{bot.strategyName} · answers from live data · can't trade on its own</div></div>
                </div>
                <Conversation key={bot.id} bot={bot} />
              </section>
            ) : <div className="hidden text-muted md:block">Pick a bot.</div>}
          </div>
        )}
      </Page>
    </Shell>
  );
}
